import { LocationService } from '@/lib/services/locationService'
import type { FreeTextLocationResult } from '@/types/location'

export type SeedDispatchLocationParams = {
  mode: 'immediate' | 'deferred'
  seedAddress?: string | null
  hasLocalStore: boolean
  hasLocalLocation: boolean
  /** Local/hydrated store already has usable lat/long. */
  localStoreHasCoordinates?: boolean
  hasInitialStore: boolean
  /** Ticket store already has usable lat/long. */
  initialStoreHasCoordinates?: boolean
  hasInitialLocation: boolean
  hydrationSkipped: boolean
}

/** Normalize a seed address for comparison and geocode attempt dedupe. */
export function normalizeSeedAddress(address?: string | null): string {
  return address?.trim() ?? ''
}

/**
 * Whether an automatically seeded dispatch location should be cleared
 * because the seed address changed to a different value.
 */
export function shouldInvalidateAutoSeededLocation(options: {
  autoSeededFromAddress: string | null
  nextSeedAddress: string
}): boolean {
  const previous = normalizeSeedAddress(options.autoSeededFromAddress)
  if (!previous) return false
  const next = normalizeSeedAddress(options.nextSeedAddress)
  return previous !== next
}

/**
 * Pick the best free-text address to geocode for FE dispatch seeding.
 * Prefer request site address, then ticket incident address, then store branch address.
 */
export function resolveSeedLocationAddress(options: {
  requestSiteAddress?: string | null
  incidentLocationAddress?: string | null
  storeBranchAddress?: string | null
}): string {
  const candidates = [
    options.requestSiteAddress,
    options.incidentLocationAddress,
    options.storeBranchAddress,
  ]
  for (const candidate of candidates) {
    const trimmed = normalizeSeedAddress(candidate)
    if (trimmed.length >= 3) return trimmed
  }
  return ''
}

function hasCoords(latitude?: number | null, longitude?: number | null): boolean {
  return (
    latitude != null &&
    Number.isFinite(Number(latitude)) &&
    longitude != null &&
    Number.isFinite(Number(longitude))
  )
}

export function storeHasCoordinates(store?: {
  latitude?: number | null
  longitude?: number | null
  location?: { latitude?: number | null; longitude?: number | null } | null
} | null): boolean {
  if (!store) return false
  return hasCoords(
    store.latitude ?? store.location?.latitude,
    store.longitude ?? store.location?.longitude,
  )
}

/**
 * Whether FE dispatch should auto-locate from a free-text address.
 * Skips only when a store/location with usable coordinates is already in place.
 * Stores without coordinates still allow address geocoding.
 */
export function canSeedDispatchLocation(params: SeedDispatchLocationParams): boolean {
  if (params.mode !== 'deferred') return false
  if (params.hydrationSkipped) return false
  if (params.hasLocalLocation) return false
  if (params.hasInitialLocation) return false

  const localStoreBlocks =
    params.hasLocalStore && (params.localStoreHasCoordinates ?? true)
  const initialStoreBlocks =
    params.hasInitialStore && (params.initialStoreHasCoordinates ?? true)
  if (localStoreBlocks || initialStoreBlocks) return false

  const address = normalizeSeedAddress(params.seedAddress)
  return address.length >= 3
}

/**
 * Geocode a free-text site address (Nominatim) and return the best match.
 */
export async function resolveDispatchSeedFromAddress(
  address: string,
  signal?: AbortSignal,
): Promise<FreeTextLocationResult | null> {
  const trimmed = normalizeSeedAddress(address)
  if (trimmed.length < 3) return null

  const locations = await LocationService.searchLocation(trimmed, signal)
  return locations[0] ?? null
}
